/**
 * Remembers which guest rounds the host has ALREADY applied to the game.
 *
 * Applying a guest round has two halves: record it locally, then tell the relay to drop it
 * from the waiting queue ("consume"). If the connection dies between the two, the round is in
 * the game AND still queued on the relay — and the next "Check for Guest Rounds" would apply
 * it a second time. Keeping this list on the host phone (written the moment a round is
 * applied, before any network call) lets the next check recognise it, skip it, and just finish
 * the tell-the-relay step. Cleared per id only once the relay has confirmed.
 */

import type { StorageLike } from './recognitionAccuracy';

const KEY_PREFIX = 'mahjong-scorer:appliedGuestRounds:';
const MAX_IDS = 200;

function defaultStorage(): StorageLike {
  return localStorage;
}

export function loadApplied(gameId: string, storage: StorageLike = defaultStorage()): Set<string> {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(KEY_PREFIX + gameId) ?? '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function write(gameId: string, ids: Set<string>, storage: StorageLike): void {
  try {
    const list = [...ids].slice(-MAX_IDS);
    if (list.length === 0) storage.removeItem(KEY_PREFIX + gameId);
    else storage.setItem(KEY_PREFIX + gameId, JSON.stringify(list));
  } catch {
    // Best effort: without it the worst case is the old behaviour, never a lost round.
  }
}

export function markApplied(gameId: string, ids: readonly string[], storage: StorageLike = defaultStorage()): void {
  if (ids.length === 0) return;
  const set = loadApplied(gameId, storage);
  for (const id of ids) set.add(id);
  write(gameId, set, storage);
}

/** The relay has confirmed these are gone from its queue, so there is nothing left to guard against. */
export function forgetApplied(gameId: string, ids: readonly string[], storage: StorageLike = defaultStorage()): void {
  if (ids.length === 0) return;
  const set = loadApplied(gameId, storage);
  for (const id of ids) set.delete(id);
  write(gameId, set, storage);
}
